import { useState } from 'react'
import { useContent } from '../content/ContentContext.jsx'
import { Em } from '../components/Em.jsx'
import { Icon } from '../components/Icon.jsx'
import { SectionNum } from '../components/SectionNum.jsx'
import { submitEnquiry } from '../lib/functions.js'

/* The "confidential brief". Submits to the submitEnquiry Cloud Function,
   which validates, rate-limits, stores the brief, and emails the house +
   a quiet auto-acknowledgement (Phase 5). A hidden honeypot field traps
   bots; real users never see or fill it. */
export function EnquirySection() {
  const { content } = useContent()
  const q = content.enquiry
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [place, setPlace] = useState('')
  const [matter, setMatter] = useState(null)
  const [horizon, setHorizon] = useState(null)
  const [words, setWords] = useState('')
  const [company, setCompany] = useState('') // honeypot
  const [done, setDone] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState(null)
  const today = new Date().toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
  const canSend = name.trim() && email.trim() && matter != null && !sending

  async function submit() {
    if (!canSend) return
    setSending(true)
    setError(null)
    try {
      await submitEnquiry({
        name,
        email,
        place,
        matter: q.matters[matter],
        horizon: horizon != null ? q.horizons[horizon] : '',
        words,
        company, // honeypot
      })
      setDone(true)
    } catch (e) {
      setError(friendly(e))
    } finally {
      setSending(false)
    }
  }

  function reset() {
    setDone(false)
    setName('')
    setEmail('')
    setPlace('')
    setMatter(null)
    setHorizon(null)
    setWords('')
    setError(null)
  }

  return (
    <section className="section enq" id="sec-enquiry">
      <SectionNum sectionKey="enquiry" />
      <h2>
        <Em pre={q.pre} em={q.em} post={q.post} />
      </h2>
      <p className="lead body-p">{q.lead}</p>

      <div className="brief">
        {done ? (
          <div className="brief-done">
            <div className="check">
              <Icon name="check" size={24} />
            </div>
            <h3>Your brief is with us.</h3>
            <p>
              It will be read in confidence, by us, and answered in kind. Thank you,{' '}
              {name.split(' ')[0] || '—'}.
            </p>
            <button className="btn-ghost" style={{ marginTop: 24 }} onClick={reset}>
              Send another
            </button>
          </div>
        ) : (
          <>
            <div className="brief-head">
              <div>
                <div className="caption" style={{ color: 'var(--sage)' }}>
                  {q.overline}
                </div>
                <div className="bt">{q.title}</div>
              </div>
              <div className="caption">{today}</div>
            </div>

            <div className="field">
              <label>Your name</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="How we should address you"
              />
            </div>
            <div className="field">
              <label>Email</label>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="A private address"
                type="email"
              />
            </div>
            <div className="field">
              <label>The house or place</label>
              <input
                value={place}
                onChange={(e) => setPlace(e.target.value)}
                placeholder="Name, estate, city"
              />
            </div>

            {/* Honeypot — visually hidden, off the tab order. Bots fill it. */}
            <div className="hp" aria-hidden="true">
              <label>
                Company
                <input
                  tabIndex={-1}
                  autoComplete="off"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                />
              </label>
            </div>

            <div className="field">
              <label>The matter</label>
              <div className="matter-grid">
                {q.matters.map((mt, i) => (
                  <button
                    key={i}
                    className={'matter' + (matter === i ? ' sel' : '')}
                    onClick={() => setMatter(i)}
                  >
                    {mt}
                  </button>
                ))}
              </div>
            </div>

            <div className="field">
              <label>The horizon</label>
              <div className="chips">
                {q.horizons.map((hz, i) => (
                  <button
                    key={i}
                    className={'chip' + (horizon === i ? ' sel' : '')}
                    onClick={() => setHorizon(i)}
                  >
                    {hz}
                  </button>
                ))}
              </div>
            </div>

            <div className="field">
              <label>In your words</label>
              <textarea
                value={words}
                maxLength={2000}
                onChange={(e) => setWords(e.target.value)}
                placeholder="A few lines are enough."
              />
              <div className="counter">{words.length} / 2000</div>
            </div>

            <div className="signoff">
              In confidence, &mdash; {name.trim() || 'your name'} &mdash;
            </div>
            {error && <div className="brief-error">{error}</div>}
            <button className="btn-send" disabled={!canSend} onClick={submit}>
              {sending ? 'SENDING…' : 'SEND BRIEF'} <Icon name="arrowRight" size={16} />
            </button>
          </>
        )}
      </div>
    </section>
  )
}

function friendly(e) {
  const msg = (e && e.message) || ''
  if (/Too many/i.test(msg)) return 'Too many submissions. Please try again later.'
  if (/email/i.test(msg)) return 'Please enter a valid email address.'
  if (/name/i.test(msg)) return 'Please tell us your name.'
  if (/matter/i.test(msg)) return 'Please choose a matter.'
  return 'Your brief could not be sent. Please try again.'
}
